import Card from '@orn/shared-web/components/Card';
import { LinkBrokenIcon } from '@orn/shared-web/icons';
import formatTimeAgo from '@orn/shared-web/utils/formatTimeAgo';

import { CalloutPriority } from '../../../../__generated__/globalTypes';
import Entity from '../../../entity';
import PlaidLinkButton from '../../../plaid/PlaidLinkButton';

import type { Callout } from '../../CalloutCard';

interface IUnhealthyBankConnectionMetadata {
  subtype: 'login_required' | 'pending_disconnect';
}

interface IUnhealthyBankConnectionCalloutCardProps {
  callout: Callout;
  className?: string;
}

export function isUnhealthyBankConnectionMetadata(metadata: unknown): metadata is IUnhealthyBankConnectionMetadata {
  if (typeof metadata !== 'object' || metadata === null || !('subtype' in metadata)) {
    return false;
  }

  const subtype = metadata.subtype;
  return subtype === 'login_required' || subtype === 'pending_disconnect';
}

function getUnhealthyBankConnectionDescription(metadata: IUnhealthyBankConnectionMetadata): string {
  if (metadata.subtype === 'login_required') {
    return 'Account needs to be re-connected';
  }
  return 'Account needs to be re-connected soon';
}

function getHeaderLabel(metadata: IUnhealthyBankConnectionMetadata): string {
  return metadata.subtype === 'login_required' ? 'Connection issue' : 'Pending connection issue';
}

const UnhealthyBankConnectionCalloutCard = ({ callout, className }: IUnhealthyBankConnectionCalloutCardProps) => {
  // Validate metadata and ensure we have bank connection info
  if (!isUnhealthyBankConnectionMetadata(callout.metadata) || !callout.bankConnection?.bank) {
    return null;
  }

  const metadata = callout.metadata;
  const bank = callout.bankConnection.bank;
  const bankConnectionId = callout.bankConnection.id;
  const lastSyncedAt = callout.bankConnection.lastSyncedAt;
  const isP0 = callout.priority === CalloutPriority.P0;
  const description = getUnhealthyBankConnectionDescription(metadata);
  const headerLabel = getHeaderLabel(metadata);

  const lastSyncText = lastSyncedAt ? `Last synced ${formatTimeAgo(lastSyncedAt)}` : 'Never synced';

  return (
    <Card variant={isP0 ? 'error' : 'warning'} className={className}>
      <Card.Header icon={<LinkBrokenIcon />} label={headerLabel} />
      <Card.Body>
        {/* Bank Row */}
        <div className="flex min-w-0 items-center gap-2">
          <Entity.Avatar entity={bank} size="sm" />
          <div className="flex min-w-0 flex-col">
            <span className="text-primary text-md truncate font-semibold">{bank.name}</span>
            <span className="text-tertiary truncate text-sm font-medium">{lastSyncText}</span>
          </div>
        </div>
        {/* Reconnect Button */}
        <div>
          <Card.Text className="truncate">{description}</Card.Text>
          <PlaidLinkButton
            bankConnectionId={bankConnectionId}
            size="xs"
            color={'primary-destructive'}
            className="mt-2 w-full"
          >
            Reconnect
          </PlaidLinkButton>
        </div>
      </Card.Body>
    </Card>
  );
};

export default UnhealthyBankConnectionCalloutCard;
